const USERNAME_COLUMNS = new Set([
  'username',
  'instagram_handle',
  'handle',
  'influencer_handle',
  'creator_handle',
  'creator',
  'profile',
]);

/**
 * Extracts the profile segment from an Instagram URL.
 */
export function extractHandleFromUrl(value: string): string {
  const match = value.match(/instagram\.com\/([^/?#\s]+)/i);
  return match ? match[1] : value;
}

/**
 * Cleans and deduplicates social media handles from raw input.
 * Strips @-prefixes, lowercases, removes invalid characters, and deduplicates.
 */
export function cleanHandles(input: string | string[]): string[] {
  const rawValues = Array.isArray(input) ? input : input.split(/[\s,]+/);

  return Array.from(
    new Set(
      rawValues
        .map((value) => extractHandleFromUrl(value.trim()))
        .map((value) => value.replace(/^@+/, '').toLowerCase())
        .map((value) => value.replace(/[^a-z0-9._-]/g, ''))
        .filter(Boolean)
    )
  );
}

/**
 * Cleans a single handle (or Instagram URL) into its canonical handle form.
 */
export function cleanHandle(input: string): string {
  return cleanHandles([input])[0] ?? '';
}

/**
 * Extracts username handles from parsed CSV rows.
 * Looks for a column named 'username', 'instagram_handle', or 'handle'.
 * @throws Error if no valid username column is found
 */
export function extractHandlesFromCsvRows(rows: Record<string, unknown>[]): string[] {
  if (rows.length === 0) {
    return [];
  }

  const headers = Object.keys(rows[0]);
  const usernameColumn = headers.find((header) =>
    USERNAME_COLUMNS.has(header.trim().toLowerCase())
  );

  if (!usernameColumn) {
    throw new Error('CSV must include a username, instagram_handle, or handle column.');
  }

  return cleanHandles(
    rows
      .map((row) => row[usernameColumn])
      .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
      .map(String)
  );
}

/**
 * Generic helper to normalize a CSV header string (lowercased, trimmed, underscores).
 */
export function normalizeCsvHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

/**
 * Generic simple CSV line parser for basic comma-separated rows.
 */
export function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}
